import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

import autoTable from 'jspdf-autotable';

export interface ParticipantData {
  id: string;
  name: string;
  email: string;
  cpf: string;
  phone?: string;
  ticket_type: string;
  ticket_value: number | string;
  payment_status: 'open' | 'confirmed' | 'paid' | 'refunded' | 'desistente';
  payment_method: 'card' | 'pix' | 'cash' | 'boleto' | null;
  created_at: string;
  dynamicAnswers?: Array<{
    question: string;
    answer: string;
  }>;
  // Campos de cupom
  coupon_id?: string;
  discount_applied?: number | string;
  original_ticket_value?: number | string;
}

export interface EventData {
  name: string;
  date?: string;
  location?: string;
  labelInteiro?: string;
  labelMeia?: string;
  labelEstudante?: string;
  labelCrianca?: string;
}

const MAX_CELL_LENGTH = 60;

const getTicketLabel = (
  type: string,
  eventData: EventData
): string => {
  switch (type) {
    case 'inteiro': return eventData.labelInteiro || 'Inteiro';
    case 'meia': return eventData.labelMeia || 'Meia';
    case 'estudante': return eventData.labelEstudante || 'Estudante';
    case 'crianca': return eventData.labelCrianca || 'Criança';
    default: return type;
  }
};
const formatCurrency = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value || 0;
  return `R$ ${numValue.toFixed(2)}`;
};

// Função auxiliar para converter valor para número
const toNumber = (value: number | string): number => {
  return typeof value === 'string' ? parseFloat(value) || 0 : value || 0;
};

// Função auxiliar para labels de método de pagamento
const getPaymentMethodLabel = (method: 'card' | 'pix' | 'cash' | 'boleto' | null) => {
  if (!method) return '-';
  switch (method) {
    case 'card': return 'Cartão';
    case 'pix': return 'PIX';
    case 'cash': return 'Dinheiro';
    case 'boleto': return 'Boleto';
    default: return method;
  }
};

// Função para exportar para Excel
export const exportToExcel = (
  participants: ParticipantData[],
  eventData: EventData
) => {
  try {
    // Calcular valores totais
    const totalValue = participants.reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const paidValue = participants
      .filter(p => p.payment_status === 'paid')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const confirmedValue = participants
      .filter(p => p.payment_status === 'confirmed')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const refundedValue = participants
      .filter(p => p.payment_status === 'refunded')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const openValue = participants
      .filter(p => p.payment_status === 'open')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);

    // Preparar dados para o Excel
    const excelData = participants.map(participant => {
      const hasDiscount = participant.discount_applied && toNumber(participant.discount_applied) > 0;
      
      const baseData: { [key: string]: any } = {
        'Nome': participant.name || '',
        'E-mail': participant.email || '',
        'CPF': participant.cpf || '',
        'Telefone': participant.phone || '',
        'Tipo de Ingresso': getTicketLabel(participant.ticket_type, eventData),
        'Valor': toNumber(participant.ticket_value),
        'Valor Original': hasDiscount ? toNumber(participant.original_ticket_value || 0) : '',
        'Desconto': hasDiscount ? toNumber(participant.discount_applied || 0) : '',
        'Usou Cupom': hasDiscount ? 'Sim' : 'Não',
        'Status Pagamento': participant.payment_status === 'paid' ? 'Pago' : 
                           participant.payment_status === 'confirmed' ? 'Confirmado' :
                           participant.payment_status === 'refunded' ? 'Estornado' : 'Em aberto',
        'Método Pagamento': getPaymentMethodLabel(participant.payment_method),
        'Data de Inscrição': participant.created_at ? new Date(participant.created_at).toLocaleDateString() : '',
      };

      // Adicionar respostas dinâmicas
      if (participant.dynamicAnswers) {
        participant.dynamicAnswers.forEach((answer, index) => {
          baseData[`${answer.question}`] = answer.answer || '';
        });
      }

      return baseData;
    });

    // Adicionar linha em branco
    excelData.push({});

    // Adicionar resumo diretamente nos dados principais
    excelData.push({
      'Nome': 'RESUMO FINANCEIRO',
      'E-mail': '',
      'CPF': '',
      'Telefone': '',
      'Tipo de Ingresso': '',
      'Valor': '',
      'Status Pagamento': '',
      'Método Pagamento': '',
      'Data de Inscrição': '',
    });

    excelData.push({
      'Nome': 'Total de Participantes',
      'E-mail': participants.length,
      'CPF': '',
      'Telefone': '',
      'Tipo de Ingresso': '',
      'Valor': '',
      'Status Pagamento': '',
      'Método Pagamento': '',
      'Data de Inscrição': '',
    });

    excelData.push({
      'Nome': 'Valor Total',
      'E-mail': formatCurrency(totalValue),
      'CPF': '',
      'Telefone': '',
      'Tipo de Ingresso': '',
      'Valor': '',
      'Status Pagamento': '',
      'Método Pagamento': '',
      'Data de Inscrição': '',
    });

    excelData.push({
      'Nome': 'Valor Pago',
      'E-mail': formatCurrency(paidValue),
      'CPF': '',
      'Telefone': '',
      'Tipo de Ingresso': '',
      'Valor': '',
      'Status Pagamento': '',
      'Método Pagamento': '',
      'Data de Inscrição': '',
    });

    excelData.push({
      'Nome': 'Valor Confirmado',
      'E-mail': formatCurrency(confirmedValue),
      'CPF': '',
      'Telefone': '',
      'Tipo de Ingresso': '',
      'Valor': '',
      'Status Pagamento': '',
      'Método Pagamento': '',
      'Data de Inscrição': '',
    });

    excelData.push({
      'Nome': 'Valor Estornado',
      'E-mail': formatCurrency(refundedValue),
      'CPF': '',
      'Telefone': '',
      'Tipo de Ingresso': '',
      'Valor': '',
      'Status Pagamento': '',
      'Método Pagamento': '',
      'Data de Inscrição': '',
    });

    excelData.push({
      'Nome': 'Valor em Aberto',
      'E-mail': formatCurrency(openValue),
      'CPF': '',
      'Telefone': '',
      'Tipo de Ingresso': '',
      'Valor': '',
      'Status Pagamento': '',
      'Método Pagamento': '',
      'Data de Inscrição': '',
    });

    // Criar workbook
    const wb = XLSX.utils.book_new();

    // Criar worksheet com os dados
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Configurar largura das colunas
    const colWidths = [
      { wch: 25 }, // Nome
      { wch: 30 }, // E-mail
      { wch: 15 }, // CPF
      { wch: 15 }, // Telefone
      { wch: 18 }, // Tipo de Ingresso
      { wch: 12 }, // Valor
      { wch: 18 }, // Status Pagamento
      { wch: 18 }, // Método Pagamento
      { wch: 15 }, // Data de Inscrição
    ];

    // Adicionar larguras para campos dinâmicos
    const dynamicQuestions = Array.from(
      new Set(
        participants
          .flatMap(p => p.dynamicAnswers || [])
          .map(ans => ans.question)
      )
    );

    dynamicQuestions.forEach(() => {
      colWidths.push({ wch: 25 });
    });

    ws['!cols'] = colWidths;

    // Adicionar worksheet ao workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Participantes');

    // Criar aba de resumo separada também
    const summaryData = [
      { 'Descrição': 'Total de Participantes', 'Valor': participants.length },
      { 'Descrição': 'Valor Total', 'Valor': formatCurrency(totalValue) },
      { 'Descrição': 'Valor Pago', 'Valor': formatCurrency(paidValue) },
      { 'Descrição': 'Valor Confirmado', 'Valor': formatCurrency(confirmedValue) },
      { 'Descrição': 'Valor Estornado', 'Valor': formatCurrency(refundedValue) },
      { 'Descrição': 'Valor em Aberto', 'Valor': formatCurrency(openValue) },
    ];

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    summaryWs['!cols'] = [{ wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Resumo');

    // Gerar nome do arquivo
    const fileName = `participantes_${eventData.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Fazer download
    XLSX.writeFile(wb, fileName);

    return { success: true, message: 'Excel exportado com sucesso!' };
  } catch (error) {
    console.error('Erro ao exportar Excel:', error);
    return { success: false, message: 'Erro ao exportar Excel' };
  }
};

// Função para exportar para PDF
export const exportToPDF = (
  participants: ParticipantData[],
  eventData: EventData
) => {
  try {
    const fileName =
      'participantes-' +
      (eventData.name || 'evento')
        .toLowerCase()
        .replace(/[^a-z0-9]/gi, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    const dynamicQuestions = Array.from(
      new Set(
        participants
          .flatMap(p => p.dynamicAnswers || [])
          .map(ans => ans.question)
      )
    );

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'A4',
    });

    // Adicionar logo no canto superior direito
    try {
      const logoImg = new Image();
      logoImg.onload = function() {
        const logoWidth = 60;
        const logoHeight = (logoImg.height * logoWidth) / logoImg.width;
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.addImage(logoImg, 'PNG', pageWidth - logoWidth - 40, 20, logoWidth, logoHeight);
      };
      logoImg.src = '/assets/logo/Marca d_agua - Aviva-05.png';
    } catch (error) {
      console.warn('Erro ao carregar logo:', error);
    }

    doc.setFontSize(22);
    doc.text(eventData.name || 'Evento', 40, 40);

    doc.setFontSize(14);
    doc.text('Lista de Participantes', 40, 65);

    const getPaymentStatusLabel = (status: 'open' | 'confirmed' | 'paid' | 'refunded' | 'desistente') => {
      switch (status) {
        case 'paid': return 'Pago';
        case 'confirmed': return 'Confirmado'; // Prestes a receber, mas ainda não recebeu
        case 'open': return 'Em aberto';
        case 'refunded': return 'Estornado'; // Devolvido
        case 'desistente': return 'Desistente';
        default: return status;
      }
    };

    const getPaymentMethodLabelPDF = (method: 'card' | 'pix' | 'cash' | 'boleto' | null) => {
      if (!method) return '-';
      switch (method) {
        case 'card': return 'Cartão';
        case 'pix': return 'PIX';
        case 'cash': return 'Dinheiro';
        case 'boleto': return 'Boleto';
        default: return method;
      }
    };

    const head = [
      [
        'Nome',
        'E-mail',
        'CPF',
        'Tipo de ingresso',
        'Valor',
        'Status Pagamento',
        'Método Pagamento',
        'Data de inscrição'
      ]
    ];

    const body = participants.flatMap(p => {
      const hasDiscount = p.discount_applied && toNumber(p.discount_applied) > 0;
      
      // Formata o valor com indicação de desconto
      const valorDisplay = hasDiscount 
        ? `${formatCurrency(p.ticket_value)} (era ${formatCurrency(p.original_ticket_value || 0)})`
        : formatCurrency(p.ticket_value);
      
      const mainRow = [
        p.name || '',
        p.email || '',
        p.cpf || '',
        getTicketLabel(p.ticket_type, eventData),
        valorDisplay,
        getPaymentStatusLabel(p.payment_status),
        getPaymentMethodLabelPDF(p.payment_method),
        p.created_at ? new Date(p.created_at).toLocaleDateString() : ''
      ];

      const rows: any[][] = [mainRow];

      // Adiciona linha de desconto se tiver cupom
      if (hasDiscount) {
        rows.push([
          {
            content: `Cupom aplicado: -${formatCurrency(p.discount_applied || 0)} de desconto`,
            colSpan: 8,
            styles: { fontStyle: 'italic', textColor: [34, 139, 34] },
            fillColor: [220, 255, 220]
          }
        ]);
      }

      // Adiciona respostas dinâmicas
      const dynamicRows = (p.dynamicAnswers || []).map(ans => [
        {
          content: `${ans.question}: ${ans.answer.length > MAX_CELL_LENGTH ? ans.answer.slice(0, MAX_CELL_LENGTH) + '...' : ans.answer}`,
          colSpan: 8,
          styles: { fontStyle: 'italic', textColor: [59, 78, 255] },
          fillColor: [220, 235, 255]
        }
      ]);

      return [...rows, ...dynamicRows];
    });

    autoTable(doc, {
      startY: 80,
      head,
      body,
      styles: {
        fontSize: 12,
        cellPadding: 6,
        halign: 'left',
        valign: 'middle',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [59, 78, 255],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [246, 248, 251],
      },
      margin: { left: 40, right: 40 },
    });

    // Calcular valores totais
    const totalValue = participants.reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const paidValue = participants
      .filter(p => p.payment_status === 'paid')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const confirmedValue = participants
      .filter(p => p.payment_status === 'confirmed')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const refundedValue = participants
      .filter(p => p.payment_status === 'refunded')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);
    const openValue = participants
      .filter(p => p.payment_status === 'open')
      .reduce((sum, p) => sum + toNumber(p.ticket_value), 0);

    const finalY = (doc as any).lastAutoTable.finalY || 80 + body.length * 20;
    
    // Adicionar título do resumo
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo Financeiro:', 40, finalY + 30);
    
    // Adicionar dados do resumo
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const summaryText = [
      `Total de Participantes: ${participants.length}`,
      `Valor Total: ${formatCurrency(totalValue)}`,
      `Valor Pago: ${formatCurrency(paidValue)}`,
      `Valor Confirmado: ${formatCurrency(confirmedValue)}`,
      `Valor Estornado: ${formatCurrency(refundedValue)}`,
      `Valor em Aberto: ${formatCurrency(openValue)}`
    ];

    let yPosition = finalY + 55;
    summaryText.forEach(text => {
      doc.text(text, 40, yPosition);
      yPosition += 20;
    });

    doc.save(`${fileName}.pdf`);
    return { success: true, message: 'PDF exportado com sucesso!' };
  } catch (error) {
    console.error('Erro ao exportar PDF:', error);
    return { success: false, message: 'Erro ao exportar PDF' };
  }
};